/**
 * Per-platform setup content for the Connect tab setup guide modal.
 * Each platform entry has an id, display label, subtitle, and a markdown
 * string containing only the steps relevant to that hardware.
 *
 * COMMON_CONTENT is always shown below the platform-specific steps and
 * covers configuration, service management, and troubleshooting.
 *
 * Update alongside docs/connect/SETUP.md.
 */

// ---------------------------------------------------------------------------
// Per-platform step content
// ---------------------------------------------------------------------------

const APPLE_SILICON = `
## Apple Silicon Mac
### Mac mini (M1/M2/M3/M4), MacBook, iMac, Mac Pro

---

### Step 1 — Install Docker Desktop

Download from [docs.docker.com/get-docker](https://docs.docker.com/get-docker/).
Open Docker Desktop and wait until it reports **Running** in the menu bar.

Allocate enough memory (Docker Desktop → Settings → Resources → Memory):

| Your machine RAM | Allocate to Docker |
|---|---|
| 16 GB | 10 GB |
| 32 GB+ | 16 GB+ |

Metal GPU acceleration is **automatic** on Apple Silicon — no extra configuration needed.

---

### Step 2 — Generate a Connect token

In **Settings → Connect** (this page), enter a device name and click **Generate Token**.
Copy the full \`sfc_…\` token — it is shown only once.

Also note your **Token ID** (shown below the token, format: \`connect://abc123…\`).
You will need this later when adding the Local provider in API Keys.

---

### Step 3 — Install the relay agent

Run this one-liner in Terminal:

\`\`\`bash
bash <(curl -sSL https://raw.githubusercontent.com/stackadoAI/stackform-connect/main/scripts/install.sh)
\`\`\`

The script will prompt for your token and a device name, then build and start the containers.

**Or install manually:**

\`\`\`bash
git clone https://github.com/stackadoAI/stackform-connect.git
cd stackform-connect
cp .env.example .env
\`\`\`

Edit \`.env\`:
\`\`\`env
STACKFORM_CONNECT_TOKEN=sfc_<your_token_id>_<your_secret>
STACKFORM_URL=https://stackform.ai
DEVICE_NAME=Mac mini
\`\`\`

Start (no GPU override needed — Metal is automatic):
\`\`\`bash
docker compose -f docker-compose.yml --env-file .env up -d
\`\`\`

---

### Step 4 — Pull a model

Choose based on your unified memory:

| Model | Memory | Quality | Best for |
|---|---|---|---|
| \`llama3.2:3b\` | ~2 GB | Good | Fast responses, light tasks |
| \`phi3.5:mini\` | ~2.2 GB | Strong reasoning | Logic, analysis |
| \`llama3.1:8b\` | ~5 GB | **Recommended** | General purpose |
| \`qwen2.5:14b\` | ~9 GB | Near-cloud quality | 16 GB+ Mac |
| \`qwen2.5:32b\` | ~20 GB | Excellent | 32 GB+ Mac only |

\`\`\`bash
# Apple Silicon: use docker exec directly (Makefile requires NVIDIA GPU)
docker exec stackform-ollama ollama pull llama3.1:8b
\`\`\`

---

### Step 5 — Verify connection

Your device should appear as **Connected** in Settings → Connect within 30 seconds.

\`\`\`bash
docker ps --filter name=stackform   # both containers should show "Up"
docker logs stackform-connect       # check for any auth errors
\`\`\`

---

### Auto-start on login

In **Docker Desktop → Settings → General**, enable
**"Start Docker Desktop when you sign in"**.
The containers use \`restart: unless-stopped\` and will come up automatically with Docker.
`;

// ---------------------------------------------------------------------------

const NVIDIA_DESKTOP = `
## NVIDIA GPU — Desktop / Server

---

### Step 1 — Install Docker Engine + NVIDIA Container Toolkit

\`\`\`bash
# Docker Engine
curl -fsSL https://get.docker.com | sh
sudo usermod -aG docker $USER   # re-login after this

# NVIDIA Container Toolkit
sudo apt-get install -y nvidia-container-toolkit
sudo nvidia-ctk runtime configure --runtime=docker
sudo systemctl restart docker
\`\`\`

Verify GPU passthrough:
\`\`\`bash
docker run --rm --gpus all nvidia/cuda:12.0-base nvidia-smi
# Should print your GPU name and driver version
\`\`\`

---

### Step 2 — Generate a Connect token

In **Settings → Connect** (this page), enter a device name and click **Generate Token**.
Copy the full \`sfc_…\` token — it is shown only once.

Also note your **Token ID** (format: \`connect://abc123…\`).

---

### Step 3 — Install the relay agent

\`\`\`bash
bash <(curl -sSL https://raw.githubusercontent.com/stackadoAI/stackform-connect/main/scripts/install.sh)
\`\`\`

The script detects your NVIDIA GPU and enables the GPU compose override automatically.

**Or install manually:**

\`\`\`bash
git clone https://github.com/stackadoAI/stackform-connect.git
cd stackform-connect
cp .env.example .env
\`\`\`

Edit \`.env\`:
\`\`\`env
STACKFORM_CONNECT_TOKEN=sfc_<your_token_id>_<your_secret>
STACKFORM_URL=https://stackform.ai
DEVICE_NAME=Home Server
\`\`\`

Start with GPU override:
\`\`\`bash
make start
\`\`\`

---

### Step 4 — Pull a model

Choose based on your GPU VRAM:

| Model | VRAM | Quality | Best for |
|---|---|---|---|
| \`llama3.2:3b\` | ~2 GB | Good | 4–6 GB cards, fast |
| \`llama3.1:8b\` | ~5 GB | **Recommended** | 8 GB+ cards |
| \`qwen2.5:14b\` | ~9 GB | Near-cloud quality | 12 GB+ cards |
| \`qwen2.5:32b\` | ~20 GB | Excellent | 24 GB+ (RTX 3090/4090) |

\`\`\`bash
make pull MODEL=llama3.1:8b
\`\`\`

---

### Step 5 — Verify connection

Your device should appear as **Connected** in Settings → Connect within 30 seconds.

\`\`\`bash
make status
make logs
\`\`\`

---

### Auto-start on boot

\`\`\`bash
sudo tee /etc/systemd/system/stackform-connect.service > /dev/null << 'EOF'
[Unit]
Description=Stackform Connect
After=docker.service
Requires=docker.service

[Service]
Type=oneshot
RemainAfterExit=yes
User=<your-username>
WorkingDirectory=/home/<your-username>/stackform-connect
ExecStart=docker compose -f docker-compose.yml -f docker-compose.gpu.yml --env-file .env up -d
ExecStop=docker compose -f docker-compose.yml -f docker-compose.gpu.yml --env-file .env down

[Install]
WantedBy=multi-user.target
EOF

sudo systemctl daemon-reload
sudo systemctl enable --now stackform-connect
\`\`\`
`;

// ---------------------------------------------------------------------------

const NVIDIA_SPARK = `
## NVIDIA Spark
### DGX Spark · GB10 Grace Blackwell Superchip

---

### Your hardware at a glance

| Component | Spec |
|---|---|
| Memory | 128 GB LPDDR5X — **unified** (CPU and GPU share the same pool) |
| GPU | GB10 Blackwell — up to 1 PFLOP FP4 |
| Architecture | ARM64 (aarch64) |
| OS | DGX OS (Ubuntu 22.04 + NVIDIA software stack) |

With 128 GB unified memory, the Spark can run models up to ~70B comfortably.
Plan ~4 GB for the OS and Docker — you have **~124 GB** available for models.
No swap configuration is needed.

---

### Step 1 — Verify Docker + NVIDIA runtime

Docker Engine and the NVIDIA Container Toolkit are **pre-installed** on DGX OS.

\`\`\`bash
docker --version
docker run --rm --gpus all nvidia/cuda:12.0-base nvidia-smi
\`\`\`

If \`nvidia-smi\` shows your Spark GPU info, you're ready. If Docker or the
NVIDIA runtime is missing for any reason, follow the official
[NVIDIA Container Toolkit install guide](https://docs.nvidia.com/datacenter/cloud-native/container-toolkit/install-guide.html).

---

### Step 2 — Generate a Connect token

In **Settings → Connect** (this page), enter a device name (e.g. \`NVIDIA Spark\`)
and click **Generate Token**. Copy the full \`sfc_…\` token — it is shown only once.

Also note your **Token ID** (format: \`connect://abc123…\`).

---

### Step 3 — Install the relay agent

\`\`\`bash
bash <(curl -sSL https://raw.githubusercontent.com/stackadoAI/stackform-connect/main/scripts/install.sh)
\`\`\`

The script detects your NVIDIA GPU and enables the GPU compose override automatically.

**Or install manually:**

\`\`\`bash
git clone https://github.com/stackadoAI/stackform-connect.git
cd stackform-connect
cp .env.example .env
\`\`\`

Edit \`.env\`:
\`\`\`env
STACKFORM_CONNECT_TOKEN=sfc_<your_token_id>_<your_secret>
STACKFORM_URL=https://stackform.ai
DEVICE_NAME=NVIDIA Spark
\`\`\`

Start with GPU override:
\`\`\`bash
make start
\`\`\`

---

### Step 4 — Pull a model

**All Ollama models are quantized** — Ollama only serves GGUF format, which is
always quantized. Pulling a tag without a suffix (e.g. \`qwen3.6:35b\`) gives you
the publisher's default quantization, typically Q4_K_M. Some older/established
models also publish explicit higher-quality variants (e.g. \`-q8_0\`); newer
models may only offer a single default tag. Run \`ollama show <model>\` after
pulling to see the exact quantization level.

The Spark's 128 GB advantage: it can run the Q8_0 variants of 70B+ models that
no consumer GPU can fit, closing the quality gap with cloud models significantly.

| Model | Tag | Memory | Quality | Notes |
|---|---|---|---|---|
| \`llama3.1:8b\` | default (Q4_K_M) | ~5 GB | Good | Fast warm-up; lots of headroom |
| \`qwen3.6:27b\` | default | ~17 GB | Excellent | Fast, strong reasoning |
| \`qwen3.6:35b\` | default | ~22 GB | Excellent | **Recommended default** |
| \`llama3.1:70b\` | default (Q4_K_M) | ~40 GB | Very good | Solid 70B baseline |
| \`llama3.1:70b-instruct-q8_0\` | Q8_0 explicit | ~70 GB | **Near-cloud quality** | Spark advantage — no consumer GPU can run this |
| \`qwen2.5:72b-instruct-q8_0\` | Q8_0 explicit | ~72 GB | **Outstanding** | Best local quality; use 128 K context (see below) |

> **Q8_0 explicit tags** are only available on select models where the publisher
> uploaded multiple quantization variants. Check the model page on
> [ollama.com/library](https://ollama.com/library) to see what tags exist before
> pulling.

> ⚠️ **Avoid \`-mlx\` tags** — e.g. \`qwen3.6:35b-mlx\`. MLX is Apple's machine
> learning framework and only runs on Apple Silicon (M1–M4). Pulling an \`-mlx\`
> model on the Spark or any NVIDIA/Linux system will fail at inference time.
> Always use the plain size tag or an explicit \`-q8_0\` tag on NVIDIA hardware.

\`\`\`bash
# Recommended starting point — strong quality, fast, fits with lots of headroom
make pull MODEL=qwen3.6:35b

# Best quality available on Spark — requires explicit q8_0 tag
make pull MODEL=qwen2.5:72b-instruct-q8_0
\`\`\`

Monitor GPU memory usage:
\`\`\`bash
nvidia-smi dmon -s mu -d 2
# Watch the USED column — should stay well below 128 GB
\`\`\`

---

### Step 4b — Context window (handled automatically)

\`make pull\` automatically sets \`num_ctx\` to **262 144 tokens** (256 K) on
every model you pull — the maximum supported by Qwen3, Gemma 4, Nemotron Nano,
and similar models.  No extra step needed.  Stackform also removes the
per-agent \`max_tokens\` cap for local models, so Ollama generates until the
model's natural stopping point.

> **Memory note (NVIDIA Spark 128 GB):** a Q8_0 72B model uses ~72 GB for
> weights + ~64 GB KV cache at 256 K → ~136 GB total — slightly over budget.
> For 72B Q8_0, use a 128 K context window (\`NUM_CTX=131072\`) to keep the
> KV cache at ~32 GB → ~104 GB total, well within the 124 GB available.
> For 32B Q8_0 at 256 K: ~34 GB weights + ~32 GB KV cache → ~66 GB total.

To use a smaller window (lower memory, faster prefill for simpler queries):

\`\`\`bash
make pull MODEL=qwen3.6:35b NUM_CTX=131072  # 128K — half the KV cache
make pull MODEL=qwen3.6:35b NUM_CTX=65536   # 64K — fast, general use
\`\`\`

To change the window on an already-pulled model (no re-download):

\`\`\`bash
make ctx MODEL=qwen3.6:35b NUM_CTX=131072
make restart
\`\`\`

| NUM_CTX | Tokens | Notes |
|---------|--------|-------|
| 262144 | 256 K | **Default** — full context for Qwen3 / Gemma 4 / Nemotron Nano |
| 131072 | 128 K | Half KV cache, still excellent for most workloads |
| 65536 | 64 K | Fast prefill, lower memory pressure |

---

### Step 5 — Verify connection

Your device should appear as **Connected** in Settings → Connect within 30 seconds.

\`\`\`bash
make status
make logs
\`\`\`

Check Ollama is using the GPU:
\`\`\`bash
docker logs stackform-ollama 2>&1 | grep -i "gpu|cuda"
# Should mention NVIDIA / CUDA
\`\`\`

---

### Step 6 — Auto-start on boot

\`\`\`bash
sudo tee /etc/systemd/system/stackform-connect.service > /dev/null << 'EOF'
[Unit]
Description=Stackform Connect
After=docker.service
Requires=docker.service

[Service]
Type=oneshot
RemainAfterExit=yes
User=<your-username>
WorkingDirectory=<path-to-stackform-connect>
ExecStart=docker compose -f docker-compose.yml -f docker-compose.gpu.yml --env-file .env up -d
ExecStop=docker compose -f docker-compose.yml -f docker-compose.gpu.yml --env-file .env down
Restart=on-failure

[Install]
WantedBy=multi-user.target
EOF

sudo systemctl daemon-reload
sudo systemctl enable --now stackform-connect
\`\`\`

Replace \`<your-username>\` and \`<path-to-stackform-connect>\` with your values.
`;

// ---------------------------------------------------------------------------

const JETSON_ORIN = `
## NVIDIA Jetson Orin
### Jetson Orin NX 16 GB · Jetson AGX Orin

---

### Your hardware at a glance

| Component | Spec |
|---|---|
| Memory | 16 GB LPDDR5 — **unified** (CPU and GPU share the same pool) |
| GPU | 1024-core Ampere, 32 Tensor cores |
| Architecture | ARM64 (aarch64) |
| OS | Ubuntu 20.04 (JetPack 5.x) or Ubuntu 22.04 (JetPack 6.x) |

Plan for ~4 GB reserved by the OS and Docker, leaving **~12 GB** for models.

---

### Step 1 — Verify JetPack version (need 5.0+)

\`\`\`bash
cat /etc/nv_tegra_release
\`\`\`

JetPack 6.x (CUDA 12.x) is preferred; JetPack 5.x (CUDA 11.4) also works.

---

### Step 2 — Verify Docker + NVIDIA runtime

Docker and the NVIDIA Container Runtime are pre-installed on JetPack 5+.

\`\`\`bash
docker --version
docker run --rm --gpus all nvcr.io/nvidia/cuda:12.6.1-base-ubuntu24.04 nvidia-smi
\`\`\`

If \`nvidia-smi\` prints your Jetson GPU info, you're ready. If not:
\`\`\`bash
sudo apt-get install -y nvidia-container-toolkit
sudo nvidia-ctk runtime configure --runtime=docker
sudo systemctl restart docker
\`\`\`

---

### Step 3 — Add swap ⚠️ Don't skip this

The default zram swap is not enough for 8B models. Add 16 GB file swap:

\`\`\`bash
sudo dd if=/dev/zero of=/swapfile bs=1M count=16384
sudo chmod 600 /swapfile
sudo mkswap /swapfile
sudo swapon /swapfile

# Make it permanent across reboots
echo '/swapfile none swap sw 0 0' | sudo tee -a /etc/fstab
\`\`\`

---

### Step 4 — Generate a Connect token

In **Settings → Connect** (this page), enter a device name (e.g. \`Jetson Orin NX\`)
and click **Generate Token**. Copy the full \`sfc_…\` token — it is shown only once.

Also note your **Token ID** (format: \`connect://abc123…\`).

---

### Step 5 — Install the relay agent

\`\`\`bash
bash <(curl -sSL https://raw.githubusercontent.com/stackadoAI/stackform-connect/main/scripts/install.sh)
\`\`\`

The script detects your NVIDIA GPU and enables the GPU compose override automatically.

**Or install manually:**

\`\`\`bash
git clone https://github.com/stackadoAI/stackform-connect.git
cd stackform-connect
cp .env.example .env
\`\`\`

Edit \`.env\`:
\`\`\`env
STACKFORM_CONNECT_TOKEN=sfc_<your_token_id>_<your_secret>
STACKFORM_URL=https://stackform.ai
DEVICE_NAME=Jetson Orin NX
\`\`\`

Start with GPU override:
\`\`\`bash
make start
\`\`\`

---

### Step 6 — Pull a model

| Model | Memory | Quality | Notes |
|---|---|---|---|
| \`llama3.2:3b\` | ~2 GB | Good | Fastest; lots of headroom |
| \`phi3.5:mini\` | ~2.2 GB | Strong reasoning | Excellent for its size |
| \`llama3.1:8b\` | ~5 GB | **Recommended** | Best balance on 16 GB |
| \`qwen2.5:14b\` | ~9 GB | Near-cloud quality | Close other apps first |

\`\`\`bash
make pull MODEL=llama3.1:8b

# Monitor unified memory while loading:
tegrastats --interval 1000
# Look for: RAM 5000/15624MB — if it approaches 15 GB, use a smaller model
\`\`\`

---

### Step 7 — Verify connection

Your device should appear as **Connected** in Settings → Connect within 30 seconds.

\`\`\`bash
make status
make logs
\`\`\`

Check Ollama is using the GPU:
\`\`\`bash
docker logs stackform-ollama 2>&1 | grep -i "gpu|cuda"
# Should mention NVIDIA / CUDA — if it says "cpu", see Troubleshooting below
\`\`\`

Expected inference speed: **15–25 tokens/s** with GPU vs. 1–3 tokens/s CPU-only.

---

### Step 8 — Auto-start on boot

\`\`\`bash
sudo tee /etc/systemd/system/stackform-connect.service > /dev/null << 'EOF'
[Unit]
Description=Stackform Connect
After=docker.service
Requires=docker.service

[Service]
Type=oneshot
RemainAfterExit=yes
User=<your-username>
WorkingDirectory=<path-to-stackform-connect>
ExecStart=docker compose -f docker-compose.yml -f docker-compose.gpu.yml --env-file .env up -d
ExecStop=docker compose -f docker-compose.yml -f docker-compose.gpu.yml --env-file .env down
Restart=on-failure

[Install]
WantedBy=multi-user.target
EOF

sudo systemctl daemon-reload
sudo systemctl enable --now stackform-connect
\`\`\`

Replace \`<your-username>\` and \`<path-to-stackform-connect>\` with your values.
`;

// ---------------------------------------------------------------------------

const WINDOWS = `
## Windows + NVIDIA GPU

---

### Step 1 — Install Docker Desktop

Download from [docs.docker.com/get-docker](https://docs.docker.com/get-docker/).
Open Docker Desktop and wait until it reports **Running** in the system tray.

Allocate enough memory (Docker Desktop → Settings → Resources → Memory):

| Your GPU VRAM | Allocate to Docker |
|---|---|
| 8 GB VRAM | 10 GB |
| 16 GB VRAM | 14 GB |
| 24 GB+ VRAM | 20 GB+ |

---

### Step 2 — Install NVIDIA Container Toolkit

Follow the official guide at
[docs.nvidia.com/datacenter/cloud-native/container-toolkit](https://docs.nvidia.com/datacenter/cloud-native/container-toolkit/install-guide.html).

Verify GPU passthrough in PowerShell:
\`\`\`powershell
docker run --rm --gpus all nvidia/cuda:12.0-base nvidia-smi
\`\`\`

---

### Step 3 — Generate a Connect token

In **Settings → Connect** (this page), enter a device name and click **Generate Token**.
Copy the full \`sfc_…\` token — it is shown only once.

Also note your **Token ID** (format: \`connect://abc123…\`).

---

### Step 4 — Install the relay agent

Run in PowerShell (no admin required):

\`\`\`powershell
irm https://raw.githubusercontent.com/stackadoAI/stackform-connect/main/scripts/install.ps1 | iex
\`\`\`

The script detects your NVIDIA GPU, prompts for your token, and starts the containers.

---

### Step 5 — Pull a model

\`\`\`powershell
# 8 GB VRAM
docker exec stackform-ollama ollama pull llama3.2:3b
# 8–12 GB VRAM — recommended default
docker exec stackform-ollama ollama pull llama3.1:8b
# 24 GB+ VRAM
docker exec stackform-ollama ollama pull qwen2.5:32b
\`\`\`

---

### Step 6 — Verify connection

Your device should appear as **Connected** in Settings → Connect within 30 seconds.

\`\`\`powershell
docker ps --filter name=stackform
docker logs stackform-connect
\`\`\`

---

### Auto-start on login

In **Docker Desktop → Settings → General**, enable
**"Start Docker Desktop when you sign in"**.
Containers use \`restart: unless-stopped\` and will start automatically with Docker.
`;

// ---------------------------------------------------------------------------
// Common content (always shown below the platform steps)
// ---------------------------------------------------------------------------

export const COMMON_CONTENT = `
## Configuring in Stackform

Once your device shows as **Connected**, complete setup in Settings:

**Add a Local provider — Settings → API Keys**
- Provider: **Local (Self-Hosted)**
- Server URL: \`connect://<token_id>\`
  *(use the Token ID shown in Settings → Connect after generating a token)*
- API Key: leave empty

**Assign to an agent — Settings → Model Defaults**
- Set any agent's provider to **Local (Self-Hosted)**
- Enter the model name exactly as shown by \`make models\`
  (e.g. \`llama3.1:8b\`)
- Save

Your next conversation will route that agent's inference through your device.

---

## Managing the Service

From the \`stackform-connect\` repo directory:

\`\`\`bash
make start                        # start containers (GPU enabled)
make stop                         # stop containers
make restart                      # restart containers
make logs                         # stream logs
make status                       # show container status
make models                       # list installed models
make pull MODEL=llama3.1:8b       # pull a model
make remove MODEL=llama3.1:8b     # remove a model (frees disk space)
make ctx MODEL=llama3.1:8b NUM_CTX=32768  # set context window size
make update                       # pull latest images and rebuild
make clean                        # stop and remove volumes (deletes ALL models)
\`\`\`

**Context window**

\`make pull\` automatically sets \`num_ctx\` to **262 144 tokens** (256 K —
the full context window for Qwen3, Gemma 4, and Nemotron Nano) on every model.
To use a smaller window or change an already-pulled model:

\`\`\`bash
make pull MODEL=llama3.1:8b NUM_CTX=65536  # pull and set 64 K window
make ctx MODEL=llama3.1:8b NUM_CTX=65536   # change an already-pulled model
make restart                               # apply the change
\`\`\`

---

**Removing a specific model**

Use \`make remove\` to delete a model you no longer need — for example, if you
want to swap out a second-slot model without stopping the service:

\`\`\`bash
make models                          # see what is installed
make remove MODEL=qwen2.5:9b         # remove just that model
make pull MODEL=llama3.1:8b          # pull the replacement
\`\`\`

Apple Silicon (no NVIDIA GPU — \`make\` targets require the GPU override):
\`\`\`bash
docker exec stackform-ollama ollama list
docker exec stackform-ollama ollama rm qwen2.5:9b
docker exec stackform-ollama ollama pull llama3.1:8b
\`\`\`

Windows (PowerShell):
\`\`\`powershell
docker exec stackform-ollama ollama list
docker exec stackform-ollama ollama rm qwen2.5:9b
docker exec stackform-ollama ollama pull llama3.1:8b
\`\`\`

> **Note:** After removing a model, go to **Settings → Model Defaults** and
> update any agent that was assigned to it, otherwise that agent will fall back
> to the Stackform default on the next run.

Script-installed users (\`install.sh\`) also have \`~/.stackform-connect/manage.sh\`
with equivalent commands. Windows users: use \`manage.ps1\`.

---

## Troubleshooting

**Device not showing as Connected**
\`\`\`bash
docker logs stackform-connect   # look for token or connection errors
docker ps --filter name=stackform   # both containers must show "Up"
\`\`\`
Check that \`STACKFORM_CONNECT_TOKEN\` in your \`.env\` file is correct and not revoked.

**Inference is very slow (< 1 token/s)**

The model is running on CPU instead of GPU:
\`\`\`bash
docker logs stackform-ollama 2>&1 | grep -E "gpu|cuda|cpu"
\`\`\`
If you see \`cpu\` only, ensure you started with \`make start\` (which includes the GPU
override) and the NVIDIA Container Toolkit is installed and configured.

Jetson Orin: add swap (see Step 3 of the Jetson guide); Spark does not need swap.

**Out of memory / model fails to load**
- Jetson Orin: add swap (see Step 3 of the Jetson guide)
- Switch to a smaller model
- Free kernel caches: \`sync && sudo sh -c 'echo 3 > /proc/sys/vm/drop_caches'\`

**Model not appearing after pulling**

Connect refreshes the model list every 30 seconds. Reload this page after a short wait.

---

## Security

- Your Connect token can only interact with the Connect relay API. It cannot read
  your chats, workflows, or account settings.
- Ollama is on the Docker internal network only. No port is published to your machine
  or the internet.
- Revoke any device token instantly from **Settings → Connect**.
- Your prompts pass through Stackform's infrastructure before reaching your local model.

---

## Uninstall

\`\`\`bash
# Stop containers
make stop

# Stop and remove model volumes (frees disk space)
make clean

# Remove the repo / install directory
rm -rf ~/.stackform-connect
\`\`\`

Windows:
\`\`\`powershell
docker compose -f "$env:USERPROFILE/.stackform-connect/docker-compose.yml" down -v
Remove-Item -Recurse -Force "$env:USERPROFILE/.stackform-connect"
\`\`\`

Then go to **Settings → Connect** and click **Revoke** next to the device entry.
`;

// ---------------------------------------------------------------------------
// Platform list — consumed by SetupGuideModal
// ---------------------------------------------------------------------------

export const PLATFORMS = [
  {
    id: 'apple',
    label: 'Apple Silicon (preview)',
    subtitle: 'Mac mini · MacBook · iMac (M1–M4)',
    content: APPLE_SILICON,
  },
  {
    id: 'nvidia',
    label: 'NVIDIA GPU',
    subtitle: 'Linux desktop or server (8 GB+ VRAM)',
    content: NVIDIA_DESKTOP,
  },
  {
    id: 'spark',
    label: 'NVIDIA Spark',
    subtitle: 'DGX Spark · 128 GB unified memory',
    content: NVIDIA_SPARK,
  },
  {
    id: 'jetson',
    label: 'Jetson Orin (preview)',
    subtitle: 'Orin NX · AGX Orin (JetPack)',
    content: JETSON_ORIN,
  },
  {
    id: 'windows',
    label: 'Windows (preview)',
    subtitle: 'NVIDIA GPU required',
    content: WINDOWS,
  },
];
